// SendCards — adult-side panel row + sheet that grants pet cards to
// a kid. Four card types (boost / spark / build / super) drive both
// the kid's spend balance AND the existing 16-state alien art (the
// first time a card of a type is spent, the matching boolean flips).
//
// Two exports:
//   default SendCards       — trigger tile + sheet bundle. Used inside
//                             KidParentPanel (the in-kid-mode gear
//                             flow). Self-mounting; controls its own
//                             open state.
//   named   SendCardsSheet  — controlled sheet only. Used from the
//                             parent-side AddKidSection → KidRow
//                             "🎁 CARDS" button so the parent can
//                             grant without context-switching into
//                             kid mode. Caller owns open/close.
//
// Sheet shape: 2x2 grid of card tiles. Each tile shows the type's
// color/emoji + the kid's current unopened count + three quick-grant
// buttons (+1, +3, +5). Tap any → grant_kid_cards RPC (inserts into
// the kid_card_grants reveal queue, NOT directly into balance —
// migration 0239), count animates up, brief "SENT" pill. Sheet stays
// open so the parent can hand out multiple types in one session.
//
// EMOJI-IN-UI EXCEPTION: this tree (src/experiments/kids-mode/**)
// is the explicit opt-in to emojis per the plan.

import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import { COLOR, FONT, RADIUS } from "../../lib/tokens";
import { KID_CARD_TYPES } from "./cardTypes";

const CARDS = KID_CARD_TYPES;
const QUICK_GRANTS = [1, 3, 5];

export default function SendCards({ userId }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      {/* Highlighted entry tile — distinct from the standard PanelRow
          shape so the grown-up's eye lands here first when opening
          the settings sheet. Same plum tint family the Magic Pen
          toggle uses (visual ownership: "this is the special
          parent-side action"). */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mcm-focusable"
        style={{
          width: "100%",
          display: "flex", alignItems: "center", gap: 14,
          padding: "16px 14px",
          marginBottom: 12,
          background: "linear-gradient(135deg, #9B5DE5 0%, #7A4E2D 100%)",
          color: "#fff",
          border: "none",
          borderRadius: 14,
          cursor: "pointer",
          textAlign: "left",
          touchAction: "manipulation",
          boxShadow: "0 4px 14px rgba(155, 93, 229, 0.35)",
        }}
      >
        <span style={{ fontSize: 32, lineHeight: 1 }}>🎁</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontFamily: FONT.itemTitle, fontStyle: "italic",
            fontSize: 18, color: "#fff", marginBottom: 2,
          }}>
            Send Cards
          </div>
          <div style={{
            fontFamily: FONT.forma, fontSize: 12,
            color: "rgba(255,255,255,0.85)", lineHeight: 1.35,
          }}>
            Give Boost · Spark · Build · Super cards to your kid's pet
          </div>
        </div>
        <span style={{ color: "rgba(255,255,255,0.7)", fontSize: 22 }}>›</span>
      </button>
      <SendCardsSheet
        open={open}
        kidUserId={userId}
        onClose={() => setOpen(false)}
      />
    </>
  );
}

// SendCardsSheet — controlled sheet. The parent-side AddKidSection
// renders this directly with a per-kid open state so the gift flow
// works without entering kid mode + PIN.
export function SendCardsSheet({ open, kidUserId, kidName, onClose }) {
  const [counts, setCounts] = useState({ boost: 0, spark: 0, build: 0, super: 0, water: 0, dairy: 0 });
  const [busyKey, setBusyKey] = useState(null);
  const [justSent, setJustSent] = useState(null);
  const [error, setError] = useState(null);

  // Load pending (unrevealed) grants when the sheet opens. Sum by
  // card_type so the tile shows "THIS MANY WAITING TO BE OPENED" —
  // the spendable deck balance lives on the kid side and is no
  // longer the right metric here. Migration 0239 reroutes
  // grant_kid_cards to insert into kid_card_grants, so this is the
  // only count the parent can do anything about.
  useEffect(() => {
    if (!open || !kidUserId) return;
    let cancelled = false;
    (async () => {
      const { data, error: e } = await supabase.rpc("get_pending_kid_card_grants", {
        p_kid_user_id: kidUserId,
      });
      if (cancelled) return;
      if (e) { setError(e.message); return; }
      const tally = { boost: 0, spark: 0, build: 0, super: 0, water: 0, dairy: 0 };
      for (const row of Array.isArray(data) ? data : []) {
        if (row.card_type in tally) tally[row.card_type] += row.count || 0;
      }
      setCounts(tally);
    })();
    return () => { cancelled = true; };
  }, [open, kidUserId]);

  const grant = async (cardKey, count) => {
    if (!kidUserId || busyKey) return;
    const tag = `${cardKey}-${count}`;
    setBusyKey(tag);
    setError(null);
    const { data: newCount, error: e } = await supabase.rpc("grant_kid_cards", {
      p_kid_user_id: kidUserId,
      p_card_type:   cardKey,
      p_count:       count,
    });
    setBusyKey(null);
    if (e) {
      setError(e.message);
      return;
    }
    setCounts((c) => ({ ...c, [cardKey]: typeof newCount === "number" ? newCount : (c[cardKey] || 0) + count }));
    setJustSent({ key: cardKey, count });
    setTimeout(() => setJustSent((cur) => (cur && cur.key === cardKey ? null : cur)), 1500);
  };

  if (!open) return null;

  return (
    <div
      onClick={() => !busyKey && onClose?.()}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(20,18,14,0.55)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 260, padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 520, maxHeight: "85vh",
          display: "flex", flexDirection: "column",
          background: "#FAF6EE",
          borderRadius: RADIUS.sheet, padding: 20,
          boxShadow: "0 -10px 32px rgba(0,0,0,0.28)",
          overflowY: "auto", WebkitOverflowScrolling: "touch",
        }}
      >
        <div style={{
          fontFamily: FONT.itemTitle, fontStyle: "italic",
          fontSize: 22, color: COLOR.ink, marginBottom: 4,
        }}>
          {kidName ? `Send pet cards to ${kidName}` : "Send pet cards"}
        </div>
        <div style={{
          fontFamily: FONT.forma, fontSize: 12, color: COLOR.muted,
          marginBottom: 16,
        }}>
          Cards wait until the kid opens kids-mode — they get a
          full-screen reveal, then the card lands in their pet deck.
        </div>

        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: 12,
          marginBottom: 12,
        }}>
          {CARDS.map((c) => {
            const count = counts[c.key] ?? 0;
            const sent = justSent?.key === c.key;
            return (
              <div
                key={c.key}
                style={{
                  position: "relative",
                  padding: 14,
                  background: "#fff",
                  border: `1.5px solid ${c.tint}55`,
                  borderRadius: 14,
                  display: "flex", flexDirection: "column", gap: 10,
                  overflow: "hidden",
                }}
              >
                <div style={{
                  position: "absolute", top: -8, right: -8,
                  width: 56, height: 56, borderRadius: 28,
                  background: `${c.tint}1a`,
                }} />
                <div style={{
                  display: "flex", alignItems: "center", gap: 10, position: "relative",
                }}>
                  <span style={{ fontSize: 30, lineHeight: 1 }}>{c.emoji}</span>
                  <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <span style={{
                      fontFamily: FONT.itemTitle, fontStyle: "italic",
                      fontSize: 18, color: COLOR.ink,
                    }}>
                      {c.label}
                    </span>
                    <span style={{
                      fontFamily: FONT.forma, fontSize: 11, color: COLOR.muted,
                    }}>
                      {c.blurb}
                    </span>
                  </div>
                </div>

                <div style={{
                  fontFamily: FONT.mono, fontSize: 11, fontWeight: 700,
                  letterSpacing: "0.08em", color: c.tint,
                }}>
                  {count > 0 ? `${count} UNOPENED` : "NONE WAITING"}
                </div>

                <div style={{ display: "flex", gap: 6 }}>
                  {QUICK_GRANTS.map((n) => {
                    const tag = `${c.key}-${n}`;
                    const isBusy = busyKey === tag;
                    return (
                      <button
                        key={n}
                        type="button"
                        onClick={() => grant(c.key, n)}
                        disabled={!!busyKey}
                        style={{
                          flex: 1,
                          padding: "8px 0",
                          background: isBusy ? c.tint : `${c.tint}14`,
                          color: isBusy ? "#fff" : c.tint,
                          border: `1.5px solid ${c.tint}`,
                          borderRadius: 10,
                          fontFamily: FONT.mono,
                          fontSize: 12, fontWeight: 700, letterSpacing: "0.08em",
                          cursor: busyKey ? "wait" : "pointer",
                          touchAction: "manipulation",
                          transition: "background 140ms ease",
                          opacity: busyKey && !isBusy ? 0.5 : 1,
                        }}
                      >
                        +{n}
                      </button>
                    );
                  })}
                </div>

                {sent && (
                  <div style={{
                    position: "absolute", top: 10, right: 10,
                    padding: "3px 8px",
                    background: c.tint, color: "#fff",
                    borderRadius: 999,
                    fontFamily: FONT.mono, fontSize: 9, fontWeight: 700,
                    letterSpacing: "0.1em",
                  }}>
                    +{justSent.count} SENT
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {error && (
          <div style={{
            marginBottom: 10,
            padding: "10px 12px",
            background: COLOR.roseDeep,
            border: `1px solid ${COLOR.roseDim}`,
            borderRadius: 8,
            color: COLOR.rose,
            fontFamily: FONT.forma, fontSize: 13,
          }}>
            {error}
          </div>
        )}

        <button
          type="button"
          onClick={() => !busyKey && onClose?.()}
          disabled={!!busyKey}
          style={{
            width: "100%", padding: 14,
            background: "transparent", color: COLOR.muted,
            border: `1px solid ${COLOR.border}`, borderRadius: 10,
            fontFamily: FONT.mono, fontSize: 12, fontWeight: 600,
            letterSpacing: "0.08em",
            cursor: busyKey ? "wait" : "pointer",
          }}
        >
          DONE
        </button>
      </div>
    </div>
  );
}
